import React, {FormEvent, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import "../../../styles/components/sign-in/sign-in-page.scss";
import TextBox from "../../common/textBox/TextBox";
import Button from "../../common/Button";
import {appRoutePaths} from "../../../config/config";
import playgroundApi from "../../../services/playgroundApi";
import {userAuthentication} from "../../../services/user/authentication";
import {recaptchaV3} from "../../../services/signIn/recaptchaV3";
import {socialLinkErrors} from "./socialLinkErrors";
import {SocialLinkHandoff, socialLinkHandoff} from "../../../services/signIn/socialLinkHandoff";
import IAuthenticationResponse from "../../../client/models/authentication/IAuthenticationResponse";

import PngSm from "../images/tapping-tablet-on-keyboard-sm.png";
import PngLg from "../images/tapping-tablet-on-keyboard-lg.png";
import WebpSm from "../images/tapping-tablet-on-keyboard-sm.webp";
import WebpLg from "../images/tapping-tablet-on-keyboard-lg.webp";
import AvifSm from "../images/tapping-tablet-on-keyboard-sm.avif";
import AvifLg from "../images/tapping-tablet-on-keyboard-lg.avif";
import s from "./SocialLinkCodePage.module.scss";

export default function SocialLinkCodePage() {
  const location = useLocation();
  const routerNavigate = useNavigate();

  const routerState = (location.state ?? {}) as Partial<SocialLinkHandoff>;
  const restored = socialLinkHandoff.restore();

  const linkingToken = routerState.linkingToken ?? restored?.linkingToken ?? null;
  const enteredEmail = routerState.enteredEmail ?? restored?.enteredEmail ?? null;
  const provider = routerState.provider ?? restored?.provider ?? null;
  const routeAfterSignIn = routerState.routeAfterSignIn ?? restored?.routeAfterSignIn;

  const [code, setCode] = useState("");

  function setVerificationCode(value: string) {
    setCode(value.replace(/\D/g, "").slice(0, 6));
  }
  const [error, setError] = useState<string | null>(null);
  const [endedMessage, setEndedMessage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const isVerifyInFlight = useRef(false);
  const [isResending, setIsResending] = useState(false);
  const isResendInFlight = useRef(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  if (!linkingToken || !enteredEmail || provider === null || endedMessage !== null) {
    return (
      <div className="sign-in-page">
        <header className="top">
          <picture>
            <source media={"(orientation: portrait)"} srcSet={AvifSm} type={"image/avif"} />
            <source media={"(orientation: portrait)"} srcSet={WebpSm} type={"image/webp"} />
            <source media={"(orientation: portrait)"} srcSet={PngSm} type={"image/png"} />
            <source media={"(orientation: landscape)"} srcSet={AvifLg} type={"image/avif"} />
            <source media={"(orientation: landscape)"} srcSet={WebpLg} type={"image/webp"} />
            <source media={"(orientation: landscape)"} srcSet={PngLg} type={"image/png"} />
            <img src={PngLg} alt="Hand reaching to tap tablet on keyboard" width={740} height={673} className={"image"} />
          </picture>
          <h1 className="overlay-text">Enter verification code</h1>
        </header>
        <div className="content-and-navigation">
          <div className="content">
            <div className="page-header-text">Enter verification code</div>
            <p className="instructions">{endedMessage ?? "Your sign-in session has expired. Please start over."}</p>
            <Button
              buttonType={"secondary"}
              onClick={() => {
                socialLinkHandoff.clear();
                routerNavigate(appRoutePaths.signInPage);
              }}
              text={"Back to sign in"}
            />
          </div>
        </div>
      </div>
    );
  }

  const signInProvider = provider;

  async function handleVerify(e?: FormEvent) {
    if (e) {
      e.preventDefault();
    }
    // The Verify button is type="submit", so click and submit both reach here.
    // A second in-flight call consumes the one-time code and fails the first.
    if (isVerifyInFlight.current || isResendInFlight.current) {
      return;
    }
    isVerifyInFlight.current = true;
    setError(null);
    setIsVerifying(true);
    let didSignIn = false;

    try {
      const result = await playgroundApi.socialLinkVerifyEmailOtc({
        linkingToken: linkingToken!,
        code,
      });

      if ("error" in result) {
        if (socialLinkErrors.isTerminalError(result.error)) {
          socialLinkHandoff.clear();
          setEndedMessage(socialLinkErrors.mapErrorToMessage(result.error));
          return;
        }
        setError(socialLinkErrors.mapErrorToMessage(result.error));
        return;
      }

      socialLinkHandoff.clear();
      await userAuthentication.completeSocialSignIn(result as IAuthenticationResponse, signInProvider);
      didSignIn = true;
      routerNavigate(socialLinkHandoff.pageAfterSignIn(routeAfterSignIn));
    } catch {
      setError(socialLinkErrors.mapErrorToMessage("request_failed"));
    } finally {
      if (!didSignIn) {
        isVerifyInFlight.current = false;
        setIsVerifying(false);
      }
    }
  }

  async function handleResend() {
    if (isResendInFlight.current || isVerifyInFlight.current) {
      return;
    }
    isResendInFlight.current = true;
    setError(null);
    setResendSuccess(false);
    setIsResending(true);
    let didSignIn = false;

    try {
      let recaptchaToken: string;
      try {
        recaptchaToken = await recaptchaV3.execute("request_email_otc");
      } catch {
        setError(socialLinkErrors.mapErrorToMessage("recaptcha_failed"));
        return;
      }

      const result = await playgroundApi.socialLinkRequestEmailOtc({
        linkingToken: linkingToken!,
        email: enteredEmail!,
        recaptchaToken,
      });

      if ("error" in result) {
        if (socialLinkErrors.isTerminalError(result.error)) {
          socialLinkHandoff.clear();
          setEndedMessage(socialLinkErrors.mapErrorToMessage(result.error));
          return;
        }
        setError(socialLinkErrors.mapErrorToMessage(result.error));
        return;
      }

      if ("next" in result) {
        setResendSuccess(true);
        return;
      }

      socialLinkHandoff.clear();
      await userAuthentication.completeSocialSignIn(result, signInProvider);
      didSignIn = true;
      routerNavigate(socialLinkHandoff.pageAfterSignIn(routeAfterSignIn));
    } catch {
      setError(socialLinkErrors.mapErrorToMessage("request_failed"));
    } finally {
      if (!didSignIn) {
        isResendInFlight.current = false;
        setIsResending(false);
      }
    }
  }

  return (
    <div className="sign-in-page">
      <header className="top">
        <picture>
          <source media={"(orientation: portrait)"} srcSet={AvifSm} type={"image/avif"} />
          <source media={"(orientation: portrait)"} srcSet={WebpSm} type={"image/webp"} />
          <source media={"(orientation: portrait)"} srcSet={PngSm} type={"image/png"} />
          <source media={"(orientation: landscape)"} srcSet={AvifLg} type={"image/avif"} />
          <source media={"(orientation: landscape)"} srcSet={WebpLg} type={"image/webp"} />
          <source media={"(orientation: landscape)"} srcSet={PngLg} type={"image/png"} />
          <img src={PngLg} alt="Hand reaching to tap tablet on keyboard" width={740} height={673} className={"image"} />
        </picture>
        <h1 className="overlay-text">Enter verification code</h1>
      </header>
      <div className="content-and-navigation">
        <div className="content">
          <div className="page-header-text">Enter verification code</div>
          <p className="instructions">
            We sent a 6-digit verification code to {enteredEmail}. Enter it below to complete sign in.
          </p>
          <form onSubmit={handleVerify} className="form">
            <TextBox
              autoComplete={"one-time-code"}
              inputMode={"numeric"}
              isErrorState={error !== null}
              errorMessages={error ? [error] : undefined}
              label={"Verification code"}
              onChange={setVerificationCode}
              placeHolder={"000000"}
              value={code}
            />
            <Button
              disabled={isResending}
              isLoading={isVerifying}
              buttonType={"secondary"}
              onClick={() => { void handleVerify(); }}
              text={isVerifying ? "Verifying…" : "Verify"}
            />
          </form>
          {resendSuccess ? (
            <p className={s.resendSuccess}>Verification code resent.</p>
          ) : null}
          <div className={s.actions}>
            <button
              type="button"
              className={s.textButton}
              onClick={() => { void handleResend(); }}
              disabled={isResending || isVerifying}
            >
              {isResending ? "Resending…" : "Resend code"}
            </button>
            <span className={s.separator}>|</span>
            <button
              type="button"
              className={s.textButton}
              onClick={() => {
                socialLinkHandoff.clear();
                routerNavigate(appRoutePaths.signInPage);
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
